import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { deviceAPI, requestAPI, statsAPI, licenseAPI, maintenanceAPI } from '../services/api';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import {
  ComputerDesktopIcon,
  TicketIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  CheckCircleIcon,
  WrenchScrewdriverIcon,
  KeyIcon,
  CalendarDaysIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

const Dashboard = () => {
  const { user, isIT } = useAuth();
  const navigate = useNavigate();
  const departmentChartRef = useRef(null);
  const typeChartRef = useRef(null);
  const [deviceStats, setDeviceStats] = useState(null);
  const [requestStats, setRequestStats] = useState(null);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [monthlyRequests, setMonthlyRequests] = useState([]);
  const [devicesByType, setDevicesByType] = useState([]);
  const [devicesByDepartment, setDevicesByDepartment] = useState([]);
  const [resolutionTime, setResolutionTime] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        if (isIT()) {
          const [deviceRes, requestRes, dashboardRes, monthlyRes, typeRes, deptRes, resTimeRes] = await Promise.all([
            deviceAPI.getStats(),
            requestAPI.getStats(),
            statsAPI.getDashboardStats().catch(() => ({ data: null })),
            statsAPI.getMonthlyRequestStats().catch(() => ({ data: [] })),
            statsAPI.getDevicesByType().catch(() => ({ data: [] })),
            statsAPI.getDevicesByDepartment().catch(() => ({ data: [] })),
            statsAPI.getAverageResolutionTime().catch(() => ({ data: null }))
          ]);
          setDeviceStats(deviceRes.data);
          setRequestStats(requestRes.data);
          setDashboardStats(dashboardRes.data);
          setMonthlyRequests(monthlyRes.data || []);
          setDevicesByType(typeRes.data || []);
          setDevicesByDepartment(deptRes.data || []);
          setResolutionTime(resTimeRes.data);
        } else {
          const requestRes = await requestAPI.getStats();
          setRequestStats(requestRes.data);
        }
      } catch (error) {
        console.error('Stats fetch error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [isIT]);

  const chartColors = [
    'rgba(59, 130, 246, 0.8)',
    'rgba(16, 185, 129, 0.8)',
    'rgba(245, 158, 11, 0.8)',
    'rgba(239, 68, 68, 0.8)',
    'rgba(139, 92, 246, 0.8)',
    'rgba(236, 72, 153, 0.8)',
    'rgba(14, 165, 233, 0.8)',
    'rgba(168, 162, 158, 0.8)',
  ];

  const monthlyChartData = {
    labels: monthlyRequests.map(m => m.month),
    datasets: [
      {
        label: 'Oluşturulan',
        data: monthlyRequests.map(m => m.created),
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 1,
      },
      {
        label: 'Çözümlenen',
        data: monthlyRequests.map(m => m.resolved),
        backgroundColor: 'rgba(16, 185, 129, 0.8)',
        borderColor: 'rgba(16, 185, 129, 1)',
        borderWidth: 1,
      },
    ],
  };

  const deviceTypeChartData = {
    labels: devicesByType.map(d => d.type),
    datasets: [{
      data: devicesByType.map(d => d.count),
      backgroundColor: chartColors,
      borderWidth: 0,
    }],
  };

  const departmentChartData = {
    labels: devicesByDepartment.map(d => d.department),
    datasets: [{
      data: devicesByDepartment.map(d => d.count),
      backgroundColor: chartColors,
      borderWidth: 0,
    }],
  };

  // Grafik tıklama işleyicileri
  const handleDepartmentChartClick = (event) => {
    const chart = departmentChartRef.current;
    if (!chart) return;

    const elements = chart.getElementsAtEventForMode(event, 'nearest', { intersect: true }, false);
    if (elements.length > 0) {
      const index = elements[0].index;
      const department = devicesByDepartment[index]?.department;
      if (department) {
        // "Atanmamış" için özel durum - zimmetsiz cihazları göster
        if (department === 'Atanmamış') {
          navigate('/devices?unassigned=true');
        } else {
          navigate(`/devices?department=${encodeURIComponent(department)}`);
        }
      }
    }
  };

  const handleTypeChartClick = (event) => {
    const chart = typeChartRef.current;
    if (!chart) return;

    const elements = chart.getElementsAtEventForMode(event, 'nearest', { intersect: true }, false);
    if (elements.length > 0) {
      const index = elements[0].index;
      const type = devicesByType[index]?.type;
      if (type) {
        navigate(`/devices?type=${encodeURIComponent(type)}`);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 dark:text-gray-100">Hoş Geldiniz, {user?.fullName}</h1>
        <p className="text-gray-600 dark:text-gray-400">IT Envanter ve Talep Yönetim Sistemi</p>
      </div>

      {/* IT/Admin Stats */}
      {isIT() && deviceStats && (
        <>
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 dark:text-gray-200 mb-4">Cihaz İstatistikleri</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900">
                  <ComputerDesktopIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-500 dark:text-gray-400 dark:text-gray-400">Toplam Cihaz</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 dark:text-gray-100">{deviceStats.total}</p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-green-100 dark:bg-green-900">
                  <CheckCircleIcon className="h-8 w-8 text-green-600 dark:text-green-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-500 dark:text-gray-400 dark:text-gray-400">Aktif Cihaz</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 dark:text-gray-100">{deviceStats.active}</p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-yellow-100 dark:bg-yellow-900">
                  <WrenchScrewdriverIcon className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-500 dark:text-gray-400 dark:text-gray-400">Bakımda</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 dark:text-gray-100">{deviceStats.maintenance}</p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-red-100 dark:bg-red-900">
                  <ExclamationTriangleIcon className="h-8 w-8 text-red-600 dark:text-red-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-500 dark:text-gray-400 dark:text-gray-400">Ömrü Dolan</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 dark:text-gray-100">{deviceStats.expiredLifespan}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Dashboard Extended Stats */}
          {dashboardStats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-orange-100">
                    <ExclamationTriangleIcon className="h-8 w-8 text-orange-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Garanti Bitecek</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{dashboardStats.devices?.expiringWarranties || 0}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-purple-100">
                    <KeyIcon className="h-8 w-8 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Lisans Bitecek</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{dashboardStats.licenses?.expiringSoon || 0}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-cyan-100">
                    <CalendarDaysIcon className="h-8 w-8 text-cyan-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Planlı Bakım</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{dashboardStats.maintenances?.scheduled || 0}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-pink-100">
                    <ChartBarIcon className="h-8 w-8 text-pink-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Ort. Çözüm Süresi</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{resolutionTime?.formatted || '-'}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Request Stats */}
      {requestStats && (
        <>
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">Talep İstatistikleri</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-blue-100">
                  <TicketIcon className="h-8 w-8 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Aktif Talepler</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{requestStats.open + requestStats.inProgress + requestStats.pending}</p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-orange-100">
                  <ClockIcon className="h-8 w-8 text-orange-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Açık Talepler</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{requestStats.open}</p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-purple-100">
                  <WrenchScrewdriverIcon className="h-8 w-8 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-500 dark:text-gray-400">İşlemde</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{requestStats.inProgress}</p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-green-100">
                  <CheckCircleIcon className="h-8 w-8 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Çözümlenen</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{requestStats.resolved + requestStats.closed}</p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Charts Section - Only for IT */}
      {isIT() && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Monthly Requests Chart */}
          {monthlyRequests.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">Aylık Talep Grafiği</h3>
              <div className="h-64">
                <Bar
                  data={monthlyChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'top',
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: {
                          stepSize: 1,
                        },
                      },
                    },
                  }}
                />
              </div>
            </div>
          )}

          {/* Devices by Type Chart */}
          {devicesByType.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">Cihaz Türleri Dağılımı</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Detay için grafiğe tıklayın</p>
              <div className="h-64 flex items-center justify-center cursor-pointer">
                <Doughnut
                  ref={typeChartRef}
                  data={deviceTypeChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'right',
                      },
                    },
                    onClick: handleTypeChartClick,
                  }}
                />
              </div>
            </div>
          )}

          {/* Devices by Department Chart */}
          {devicesByDepartment.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">Departman Bazlı Cihazlar</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Detay için grafiğe tıklayın</p>
              <div className="h-64 flex items-center justify-center cursor-pointer">
                <Doughnut
                  ref={departmentChartRef}
                  data={departmentChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'right',
                      },
                    },
                    onClick: handleDepartmentChartClick,
                  }}
                />
              </div>
            </div>
          )}

          {/* Quick Summary */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">Özet Bilgiler</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                <span className="text-gray-600">Atanmış Cihazlar</span>
                <span className="font-semibold">{dashboardStats?.devices?.assigned || deviceStats?.assigned || 0}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                <span className="text-gray-600">Bu Ay Çözümlenen</span>
                <span className="font-semibold">{dashboardStats?.requests?.resolvedThisMonth || 0}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                <span className="text-gray-600">Değişim Gereken</span>
                <span className="font-semibold text-red-600">{dashboardStats?.devices?.needReplacement || 0}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                <span className="text-gray-600">Açık Talep</span>
                <span className="font-semibold text-orange-600">{dashboardStats?.requests?.open || requestStats?.open || 0}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">Hızlı İşlemler</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Link
          to="/requests/new"
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 hover:shadow-lg transition-shadow border-l-4 border-blue-500"
        >
          <h3 className="font-semibold text-gray-900">Yeni Talep Oluştur</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Yeni cihaz, yazılım veya yetki talebi</p>
        </Link>

        <Link
          to="/requests/new?type=issue"
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 hover:shadow-lg transition-shadow border-l-4 border-red-500"
        >
          <h3 className="font-semibold text-gray-900">Arıza Bildir</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Cihaz veya sistem arızası bildirin</p>
        </Link>

        {isIT() && (
          <>
            <Link
              to="/devices/new"
              className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 hover:shadow-lg transition-shadow border-l-4 border-green-500"
            >
              <h3 className="font-semibold text-gray-900">Yeni Cihaz Ekle</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Envantere yeni cihaz ekle</p>
            </Link>

            <Link
              to="/licenses"
              className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 hover:shadow-lg transition-shadow border-l-4 border-purple-500"
            >
              <h3 className="font-semibold text-gray-900">Lisans Yönetimi</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Yazılım lisanslarını yönet</p>
            </Link>

            <Link
              to="/maintenances"
              className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 hover:shadow-lg transition-shadow border-l-4 border-cyan-500"
            >
              <h3 className="font-semibold text-gray-900">Bakım Takibi</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Planlı bakımları yönet</p>
            </Link>

            <Link
              to="/devices"
              className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 hover:shadow-lg transition-shadow border-l-4 border-indigo-500"
            >
              <h3 className="font-semibold text-gray-900">Cihaz Listesi</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Tüm cihazları görüntüle</p>
            </Link>

            <Link
              to="/requests"
              className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 hover:shadow-lg transition-shadow border-l-4 border-yellow-500"
            >
              <h3 className="font-semibold text-gray-900">Talepleri Yönet</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Açık talepleri görüntüle ve yönet</p>
            </Link>
          </>
        )}

        <Link
          to="/requests"
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 hover:shadow-lg transition-shadow border-l-4 border-gray-500"
        >
          <h3 className="font-semibold text-gray-900">Taleplerim</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Taleplerimin durumunu takip et</p>
        </Link>
      </div>
    </div>
  );
};

export default Dashboard;
